import React from 'react';
import { useDispatch } from 'react-redux';
import {
    actionCreators as aiActionCreators
} from '../../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import { hidePopoverAndBlur, useBootstrapPopover } from '../hooks/useBootstrapPopover';
import { useLocalizer } from '../hooks/useLocalizer';

interface StartOverWithNewFilesButtonProps {
    onStartOver?: () => void;
    setErrorMessage: (message: string | null) => void;
    title?: string;
    description?: string;
    appearance?: 'resolution' | 'action';
    isPrimary?: boolean;
}

export const StartOverWithNewFilesButton: React.FC<StartOverWithNewFilesButtonProps> = ({
    onStartOver,
    setErrorMessage,
    title = 'Start over with new files',
    description = 'Process different or additional documents.',
    appearance = 'resolution',
    isPrimary = false
}) => {
    const localize = useLocalizer();
    const dispatch = useDispatch();
    const [isStartingOver, setIsStartingOver] = React.useState(false);
    const localizedTitle = localize(title);
    const localizedDescription = localize(description);
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);

    useBootstrapPopover(buttonRef, isStartingOver || appearance !== 'resolution');

    const startOverWithNewFiles = () => {
        try {
            hidePopoverAndBlur(buttonRef);
            setIsStartingOver(true);
            setErrorMessage(null);

            dispatch(aiActionCreators.clearAIOperation() as any);
            onStartOver?.();
        } catch {
            setIsStartingOver(false);
            setErrorMessage(localize('Sorry, there was a problem starting over with new files. Please try again.'));
        }
    };

    if (appearance === 'action') {
        return (
            <button
                type="button"
                className={`btn ${isPrimary ? 'btn-toxnot-primary' : 'btn-default'} gap-summary-process-new-documents`}
                disabled={isStartingOver}
                onClick={startOverWithNewFiles}
            >
                {localizedTitle}
            </button>
        );
    }

    return (
        <button
            ref={buttonRef}
            type="button"
            className="gap-summary-resolution-option gap-summary-resolution-muted"
            data-toggle={isStartingOver ? undefined : 'popover'}
            data-container={isStartingOver ? undefined : 'body'}
            data-placement={isStartingOver ? undefined : 'bottom'}
            data-content={isStartingOver ? undefined : localizedDescription}
            aria-label={`${localizedTitle}. ${localizedDescription}`}
            disabled={isStartingOver}
            onClick={startOverWithNewFiles}
        >
            <span className="gap-summary-resolution-icon">
                <span className="glyphicon glyphicon-upload" aria-hidden="true"></span>
            </span>
            <span className="gap-summary-resolution-copy">
                <strong>{localizedTitle}</strong>
            </span>
            <span className="glyphicon glyphicon-arrow-right gap-summary-resolution-arrow" aria-hidden="true"></span>
        </button>
    );
};

export default StartOverWithNewFilesButton;
